import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@contexts/AuthContext';
import io, { Socket } from 'socket.io-client';
import { API_ORIGIN } from '@config/api';

export const useReceptionSocket = (
  clinicId: string,
  onRefresh: () => void
) => {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);

  /**
   * Handler pentru evenimente de update programari
   */
  const handleAppointmentsUpdated = useCallback((_data: { date: string; changedIds: string[] }) => {
    onRefresh();
  }, [onRefresh]);

  /**
   * Handler pentru schimbari de status vizita
   */
  const handleStatusChanged = useCallback((_data: { 
    appointmentId: string; 
    status: string; 
    timestamps: any 
  }) => {
    onRefresh();
  }, [onRefresh]);

  /**
   * Handler pentru check-in creat
   */
const handleCheckinCreated = useCallback((_data: { 
  appointmentId: string; 
  arrivedAt: string 
}) => {
    onRefresh();
  }, [onRefresh]);

  /**
   * Handler pentru alerte intarzieri
   */
  const handleDelayAlert = useCallback((_data: { 
    appointmentId: string; 
    delayMinutes: number 
  }) => {
    onRefresh();
  }, [onRefresh]);

  useEffect(() => {
    if (!clinicId || !user) return;

    // Conecteaza la WebSocket
    const socket = io(API_ORIGIN, {
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem('token')
      }
    });

    socketRef.current = socket;

    // Subscribe la canalul clinicii
    const channel = `reception:${clinicId}`;
    socket.emit('subscribe', { channel });

    // Inregistreaza event listeners
    socket.on('appointments:updated', handleAppointmentsUpdated);
    socket.on('visit:statusChanged', handleStatusChanged);
    socket.on('checkin:created', handleCheckinCreated);
    socket.on('alerts:delayed', handleDelayAlert);

    // Handler pentru erori
    socket.on('error', (error) => {
      console.error('❌ Eroare WebSocket:', error);
    });

    // Handler pentru deconectare
    socket.on('disconnect', () => {
      // Deconectat de la WebSocket
    });

    // Cleanup la unmount
    return () => {
      socket.emit('unsubscribe', { channel });
      socket.off('appointments:updated', handleAppointmentsUpdated);
      socket.off('visit:statusChanged', handleStatusChanged);
      socket.off('checkin:created', handleCheckinCreated);
      socket.off('alerts:delayed', handleDelayAlert);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    clinicId, 
    user,
    handleAppointmentsUpdated,
    handleStatusChanged,
    handleCheckinCreated,
    handleDelayAlert
  ]);

  /**
   * Emit un eveniment custom
   */
  const emit = useCallback((event: string, data: any) => {
    if (socketRef.current) {
      socketRef.current.emit(event, data);
    }
  }, []);

  return {
    socket: socketRef.current,
    emit,
    isConnected: socketRef.current?.connected || false
  };
};

